import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { ProgressBar } from "../components/ProgressBar";
import { api } from "../lib/api";
import { useT } from "../i18n";
import { ArrowLeft, MapPin, Users, BookOpen, Phone, TrendingUp, X, FileText } from "lucide-react";
import { XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Area, AreaChart } from "recharts";

export default function SchoolDetail() {
  const { schoolId } = useParams();
  const { t } = useT();
  const [s, setS] = useState(null);
  const [trend, setTrend] = useState([]);
  const [mouOpen, setMouOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/public/schools/${schoolId}`).then(r => setS(r.data)).catch(() => setS(false));
    api.get(`/public/schools/${schoolId}/trend`).then(r => setTrend(r.data.trend || [])).catch(() => setTrend([]));
  }, [schoolId]);

  if (s === null) return <PublicLayout><div className="p-20 text-center text-[var(--bb-muted)]">Loading…</div></PublicLayout>;
  if (s === false) return <PublicLayout><div className="p-20 text-center">School not found.</div></PublicLayout>;

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-24">
        <Link to="/schools" className="bb-link text-sm inline-flex items-center gap-2 mb-8" data-testid="back-to-schools"><ArrowLeft size={14}/> All schools</Link>

        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <div className="flex items-start gap-4 mb-5">
              {s.logo_path && (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${s.logo_path}`} alt="" className="w-16 h-16 rounded-full object-cover border border-[var(--bb-border)] shrink-0"/>
              )}
              <div>
                <div className="bb-tag bb-tag-green mb-3">{s.status}</div>
                <h1 className="font-serif text-5xl md:text-6xl text-[var(--bb-forest)] tracking-tight leading-[1.05]" data-testid="school-name">{s.name}</h1>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-5 text-sm text-[var(--bb-muted)]">
              <div className="flex items-center gap-2"><MapPin size={14}/> {s.address}</div>
              {s.contact_person && <div className="flex items-center gap-2"><Phone size={14}/> {s.contact_person}</div>}
            </div>

            <div className="mt-10 bb-card p-8">
              <div className="bb-overline text-[var(--bb-muted)] mb-4">FUNDING PROGRESS</div>
              <ProgressBar value={s.total_books_funded || 0} total={s.total_books_required || 1} testId="school-detail-progress"/>
              <div className="mt-6 grid grid-cols-3 divide-x divide-[var(--bb-border)]">
                <Fact icon={<Users size={14}/>} label="Students" value={s.total_students}/>
                <Fact icon={<BookOpen size={14}/>} label="Books required" value={s.total_books_required}/>
                <Fact icon={<BookOpen size={14}/>} label="Books funded" value={s.total_books_funded || 0}/>
              </div>
            </div>

            <div className="mt-6 bb-card p-8" data-testid="school-trend-chart">
              <div className="bb-overline text-[var(--bb-muted)] mb-4 flex items-center gap-2"><TrendingUp size={14}/> IMPACT OVER TIME</div>
              {trend.length === 0 ? (
                <div className="text-sm text-[var(--bb-muted)] p-6 text-center">The chart fills in as donations arrive.</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={trend}>
                    <defs>
                      <linearGradient id="bbArea" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#C25946" stopOpacity={0.4}/>
                        <stop offset="100%" stopColor="#C25946" stopOpacity={0.02}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#E8E3D7" strokeDasharray="3 3"/>
                    <XAxis dataKey="month" stroke="#6C7A73" fontSize={11}/>
                    <YAxis stroke="#6C7A73" fontSize={11}/>
                    <Tooltip/>
                    <Area type="monotone" dataKey="books" stroke="#C25946" strokeWidth={2} fill="url(#bbArea)" name="Books sponsored"/>
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="mt-6 bb-card p-8">
              <div className="bb-overline text-[var(--bb-muted)] mb-3">WHY BALA BASAVA</div>
              <p className="text-[var(--bb-ink)]/80 leading-relaxed">
                The Bala Basava comic series retells the 12th-century reformer Basavanna's teachings of equality and dignity for children in Kannada and English. Every set includes 12 illustrated stories designed for ages 7 to 14.
              </p>
            </div>

            {s.mou_path && (
              <div className="mt-6 bb-card p-8" data-testid="school-mou-block">
                <div className="bb-overline text-[var(--bb-muted)] mb-3 flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  MOU DOCUMENT
                </div>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-medium text-[var(--bb-forest)]">Memorandum of Understanding</div>
                    <div className="text-xs text-[var(--bb-muted)] mt-1">Signed agreement between the school and Bala Basava Movement.</div>
                    {s.mou_uploaded_at && <div className="text-[10px] text-[var(--bb-muted)] mt-1">Uploaded {s.mou_uploaded_at.slice(0,10)}</div>}
                  </div>
                  <button
                    onClick={() => setMouOpen(true)}
                    className="bb-btn-secondary inline-flex items-center gap-2 !py-2 !px-4 text-sm"
                    data-testid="view-mou"
                  >
                    <FileText size={14}/> {t("mou_view")}
                  </button>
                </div>
              </div>
            )}
          </div>

          <aside className="md:col-span-5">
            <div className="bb-card p-8 sticky top-28">
              <div className="bb-overline text-[var(--bb-terracotta)] mb-3">SPONSOR BOOKS FOR A SCHOOL</div>
              <p className="text-sm text-[var(--bb-ink)]/85 leading-relaxed mb-5">
                Support the distribution of Bala Basava comics to students by sponsoring book sets.
                Each sponsorship represents the purchase of educational comics that are delivered to schools.
              </p>
              <ul className="text-sm text-[var(--bb-ink)]/80 space-y-2 mb-6" data-testid="sponsor-trust-bullets">
                <li>· Secure payment via Razorpay</li>
                <li>· Fixed pricing per book set</li>
                <li>· Delivered to schools for student access</li>
              </ul>
              <button
                onClick={() => navigate(`/donate/${s.school_id}`)}
                className="bb-btn-primary w-full"
                data-testid="donate-cta"
              >Sponsor Books</button>
              <div className="text-[10px] text-[var(--bb-muted)] mt-3 text-center tracking-wider uppercase">You will receive confirmation of your sponsorship</div>
            </div>
          </aside>
        </div>
      </section>

      {mouOpen && s.mou_path && (
        <MouModal
          url={`${process.env.REACT_APP_BACKEND_URL}/api/files/${s.mou_path}`}
          uploadedAt={s.mou_uploaded_at}
          onClose={() => setMouOpen(false)}
          t={t}
        />
      )}
    </PublicLayout>
  );
}

function MouModal({ url, uploadedAt, onClose, t }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-3 md:p-6 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="mou-modal">
      <div className="bg-white rounded-md w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-[var(--bb-border)]">
          <div>
            <div className="bb-overline text-[var(--bb-terracotta)] mb-1">{t("mou_section")}</div>
            <h3 className="font-serif text-xl text-[var(--bb-forest)] leading-tight">{t("mou_view_title")}</h3>
            <p className="text-xs text-[var(--bb-muted)] mt-1">{t("mou_view_sub")}</p>
            {uploadedAt && <div className="text-[10px] text-[var(--bb-muted)] mt-1">{uploadedAt.slice(0,10)}</div>}
          </div>
          <div className="flex items-center gap-2">
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="bb-btn-secondary !py-1.5 !px-3 text-xs"
              data-testid="mou-open-new-tab"
            >Open in new tab</a>
            <button
              onClick={onClose}
              className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--bb-sand)] text-[var(--bb-muted)] hover:text-[var(--bb-ink)]"
              data-testid="mou-close"
              aria-label="Close"
            >
              <X size={18}/>
            </button>
          </div>
        </div>
        <iframe
          src={url}
          title="MoU PDF"
          className="flex-1 w-full"
          data-testid="mou-iframe"
        />
      </div>
    </div>
  );
}

function Fact({ icon, label, value }) {
  return (
    <div className="px-4 first:pl-0">
      <div className="bb-overline text-[var(--bb-muted)] flex items-center gap-1">{icon} {label}</div>
      <div className="font-serif text-2xl text-[var(--bb-forest)] mt-1">{value?.toLocaleString() ?? "—"}</div>
    </div>
  );
}
